import { useEffect, useState } from 'react';
import { Icon } from './Icon';
import {
  disablePush,
  enablePush,
  getPushState,
  isIos,
  sendTestPush,
  type PushState,
} from './push';

/** 알림 켜기/끄기 + 상태 안내 바텀시트. 두 사용자 모두 사용(계정별 구독). */
export function NotificationSettings({ onClose }: { onClose: () => void }) {
  const [state, setState] = useState<PushState | null>(null);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);

  useEffect(() => {
    getPushState()
      .then(setState)
      .catch(() => setState('unsupported'));
  }, []);

  async function toggle() {
    setBusy(true);
    setMsg(null);
    try {
      if (state === 'subscribed') {
        setState(await disablePush());
      } else {
        const next = await enablePush();
        setState(next);
        if (next === 'denied') {
          setMsg(
            isIos()
              ? '알림이 차단돼 있어요. 기기의 설정 > 알림에서 이 앱을 허용해주세요.'
              : '알림이 차단돼 있어요. 브라우저 사이트 설정에서 허용해주세요.',
          );
        }
      }
    } catch (e) {
      setMsg(e instanceof Error ? e.message : '문제가 생겼어요.');
    } finally {
      setBusy(false);
    }
  }

  async function test() {
    setBusy(true);
    setMsg(null);
    try {
      await sendTestPush();
      setMsg('테스트 알림을 보냈어요. 잠시 후 도착해요.');
    } catch {
      setMsg('테스트 발송에 실패했어요.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="fixed inset-0 z-40 flex items-end bg-black/30" onClick={onClose}>
      <div
        className="mx-auto w-full max-w-md rounded-t-3xl border-t border-line bg-card p-5 pb-safe"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="mb-3 flex items-center justify-between">
          <h2 className="flex items-center gap-2 font-round text-lg font-bold text-ink">
            <Icon name="Bell" size={20} className="text-accentA" />
            알림
          </h2>
          <button
            onClick={onClose}
            className="flex h-9 w-9 items-center justify-center rounded-full text-ink-soft hover:bg-surface-soft"
          >
            <Icon name="X" size={20} />
          </button>
        </div>

        <p className="mb-4 text-sm leading-relaxed text-ink-soft">
          상대가 유령체스 수를 둬서 <b>내 차례</b>가 되거나, <b>새 편지·타임캡슐</b>이 도착하면
          알려드려요.
        </p>

        <Body
          state={state}
          busy={busy}
          onToggle={toggle}
          onTest={test}
        />

        {msg && <p className="mt-3 text-sm text-accentA">{msg}</p>}
      </div>
    </div>
  );
}

function Body({
  state,
  busy,
  onToggle,
  onTest,
}: {
  state: PushState | null;
  busy: boolean;
  onToggle: () => void;
  onTest: () => void;
}) {
  if (state === null) {
    return <p className="text-sm text-ink-soft">상태를 확인하는 중…</p>;
  }

  if (state === 'needs-install') {
    return (
      <div className="rounded-2xl bg-surface-soft p-4 text-sm leading-relaxed text-ink-soft">
        아이폰에서는 <b>홈 화면에 추가</b>한 뒤에만 알림을 켤 수 있어요.
        <br />
        공유 버튼 → <b>홈 화면에 추가</b> → 추가된 앱으로 열고 다시 시도해주세요.
      </div>
    );
  }

  if (state === 'unsupported') {
    return (
      <div className="rounded-2xl bg-surface-soft p-4 text-sm text-ink-soft">
        이 브라우저는 알림을 지원하지 않아요.
      </div>
    );
  }

  const on = state === 'subscribed';
  return (
    <div className="space-y-3">
      <button
        disabled={busy}
        onClick={onToggle}
        className={`flex w-full items-center justify-center gap-2 rounded-2xl px-4 py-3 font-semibold tap-bounce disabled:opacity-50 ${
          on ? 'bg-surface-soft text-ink' : 'bg-accentA text-white'
        }`}
      >
        <Icon name={on ? 'BellOff' : 'Bell'} size={18} />
        {on ? '알림 끄기' : '알림 켜기'}
      </button>

      {on && (
        <button
          disabled={busy}
          onClick={onTest}
          className="w-full rounded-2xl border border-line px-4 py-2.5 text-sm text-ink-soft tap-bounce disabled:opacity-50"
        >
          테스트 알림 보내기
        </button>
      )}

      {state === 'denied' && (
        <p className="text-sm text-ink-soft">
          현재 차단돼 있어요. 기기의 설정 &gt; 알림에서 이 앱을 허용해야 켤 수 있어요.
        </p>
      )}
    </div>
  );
}
